import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Keeps agenda header, body, and time column in sync,
 * and reports the body vertical scrollbar width for header alignment.
 * Pass observeKey so measurement re-attaches after the calendar mounts.
 */
export default function useSyncedHorizontalScroll(observeKey) {
  const headerScrollRef = useRef(null);
  const mainScrollRef = useRef(null);
  const timeScrollRef = useRef(null);
  const contentRef = useRef(null);
  const syncingHorizontalRef = useRef(false);
  const syncingVerticalRef = useRef(false);
  const [verticalScrollbarWidth, setVerticalScrollbarWidth] = useState(0);

  const measureOverflow = useCallback(() => {
    const main = mainScrollRef.current;
    const content = contentRef.current;
    if (!main || !content) {
      setVerticalScrollbarWidth(0);
      return;
    }

    setVerticalScrollbarWidth(Math.max(0, main.offsetWidth - main.clientWidth));
  }, []);

  const syncHorizontalFrom = useCallback((source) => {
    if (!source || syncingHorizontalRef.current) return;
    const left = source.scrollLeft;
    syncingHorizontalRef.current = true;
    [headerScrollRef.current, mainScrollRef.current].forEach((target) => {
      if (target && target !== source && target.scrollLeft !== left) {
        target.scrollLeft = left;
      }
    });
    syncingHorizontalRef.current = false;
  }, []);

  const syncVerticalFrom = useCallback((source) => {
    if (!source || syncingVerticalRef.current) return;
    const top = source.scrollTop;
    syncingVerticalRef.current = true;
    [timeScrollRef.current, mainScrollRef.current].forEach((target) => {
      if (target && target !== source && target.scrollTop !== top) {
        target.scrollTop = top;
      }
    });
    syncingVerticalRef.current = false;
  }, []);

  const handleHeaderScroll = useCallback(() => {
    syncHorizontalFrom(headerScrollRef.current);
  }, [syncHorizontalFrom]);

  const handleMainScroll = useCallback(() => {
    syncHorizontalFrom(mainScrollRef.current);
    syncVerticalFrom(mainScrollRef.current);
  }, [syncHorizontalFrom, syncVerticalFrom]);

  const handleTimeScroll = useCallback(() => {
    syncVerticalFrom(timeScrollRef.current);
  }, [syncVerticalFrom]);

  useLayoutEffect(() => {
    measureOverflow();
  }, [measureOverflow, observeKey]);

  useEffect(() => {
    const main = mainScrollRef.current;
    const content = contentRef.current;
    if (!main || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      measureOverflow();
    });

    observer.observe(main);
    if (content) observer.observe(content);

    window.addEventListener('resize', measureOverflow);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measureOverflow);
    };
  }, [measureOverflow, observeKey]);

  return {
    headerScrollRef,
    mainScrollRef,
    timeScrollRef,
    contentRef,
    verticalScrollbarWidth,
    handleHeaderScroll,
    handleMainScroll,
    handleTimeScroll,
    measureOverflow,
  };
}
